import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Form, Formik } from 'formik'
import { useTranslation, Trans } from 'react-i18next'
import { isEmpty } from 'ramda'
import * as Yup from 'yup'
import styled from 'styled-components'
import { Dialog, IconInfoInverted, Textarea, Button, Inline } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { colors } from '@/styles'
import useCurrentSession from '@/hooks/use-current-session'
import config from '@/config'

const FieldInputContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const AsteriskWrapper = styled.span`
  color: ${colors.primary};
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;

  button {
    height: 44px;
  }
`

const DescriptionWrapper = styled.p`
  margin: 0;
  padding-bottom: 32px;
`

const TipWrapper = styled.div`
  display: flex;
  padding: 16px 0 32px;
`

const TipContentWrapper = styled.div`
  display: inline;
  color: ${vars.foregroundColor.info};
  font-size: 14px;
  font-weight: 500;
  margin-left: 8px;

  a {
    display: inline;
    color: ${vars.foregroundColor.info};
    text-decoration: underline;
    font-weight: 700;
  }
`

const NoCreditHistoryModal = ({ visible, onClose, updateUser }) => {
  const { APP_PATH } = config
  const session = useCurrentSession()
  const initialValues = {
    no_income_reason: session?.user_questions?.no_income_reason?.answer || ''
  }

  const validationSchema = Yup.object().shape({
    no_income_reason: Yup.string().required()
  })
  const { t } = useTranslation()
  const [message, setMessage] = useState(initialValues.no_income_reason)
  const [submitError, setSubmitError] = useState(false)
  const dispatch = useDispatch()
  const handleSubmit = (errors, values, message) => {
    if (errors.no_income_reason === 'Required') setSubmitError(true)
    if (isEmpty(errors) && !isEmpty(values) && !isEmpty(message)) {
      updateUser({ international_user: 1, no_income_reason: values.no_income_reason })
      onClose()
      dispatch.profile.load()
    }
  }

  return (
    <Dialog
      id="noCreditHistoryModal"
      open={visible}
      onClose={onClose}
      title={
        <span>
          {t('r.profile.edit.no_credit_history.title', 'Please provide more details.')}
          <AsteriskWrapper> *</AsteriskWrapper>
        </span>
      }
    >
      <DescriptionWrapper>
        {t(
          'r.profile.edit.no_credit_history.text',
          'New to Canada? Share insights about your financial history here. Landlords will review this information to assess your ability to meet rent payments when reviewing your application.'
        )}
      </DescriptionWrapper>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        validateOnMount={true}
        enableReinitialize
      >
        {({ errors, values, setFieldValue }) => {
          if (values.no_income_reason.length !== 0) setSubmitError(false)
          return (
            <Form>
              <FieldInputContainer>
                <Textarea
                  name="no_income_reason"
                  tone={submitError ? 'critical' : 'neutral'}
                  message={
                    !isEmpty(errors) && !isEmpty(values) && !isEmpty(message) ? 'required' : null
                  }
                  placeholder={t(
                    'r.profile.edit.no_credit_history.placeholder',
                    "I'm new to the country, but I'd be happy to provide additional documents like bank statements to prove I will be able to pay rent on time every month."
                  )}
                  onChange={e => {
                    setMessage(e.target.value)
                    setFieldValue('no_income_reason', e.target.value, true)
                  }}
                  value={message}
                  characterLimit={200}
                />
              </FieldInputContainer>
              <TipWrapper>
                <IconInfoInverted size={'large'} tone={'info'} />
                <TipContentWrapper>
                  <Trans i18nKey="r.profile.edit_no_credit_history_tip">
                    Tip: To help support your status in Canada, we recommend uploading financial
                    documents (ie. bank statement) with your application.
                    <a
                      href={`${APP_PATH}/profile/trust-score/edit`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Add documents here
                    </a>{' '}
                  </Trans>
                </TipContentWrapper>
              </TipWrapper>
              <ButtonContainer>
                <Inline space="small" collapseBelow="desktop">
                  <Button
                    variant="solid"
                    size="small"
                    onClick={() => handleSubmit(errors, values, message)}
                  >
                    {t('common.submit', 'Submit')}
                  </Button>
                </Inline>
              </ButtonContainer>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export default NoCreditHistoryModal
